import { parseE164 } from './phone';

/**
 * Pure helpers shared by the identity-first entry (ClientIdentityFirst) and the
 * phone-verify step (SignupPhoneVerify). Kept out of the components so the
 * branchy validation/error logic is unit-testable without mounting React.
 */

// Treat input as a phone number only when it contains NO letters (digits / + / spaces /
// ()-. only). Emails always contain letters, so once the user types a letter we stay in
// email mode — even if the local part has digits (e.g. "name2@…"). A phone never has letters.
export function looksLikePhone(value) {
  return !!value && !value.includes('@') && !/[a-zA-Z]/.test(value) && /\d/.test(value);
}

/**
 * Resolve a raw entry-field value into a canonical identifier, or an error string.
 * Emails are lowercased; phone-ish input is parsed to E.164 using the picked country.
 * Returns { identifier } on success or { error } on failure.
 */
export function resolveIdentifier(rawValue, countryCode, mode) {
  const value = (rawValue || '').trim();
  // `mode` ('email' | 'phone') comes from the explicit entry the person chose; each
  // field accepts only its own kind. Without a mode (legacy combined field) the
  // presence of '@' decides.
  if (mode === 'email') {
    if (!value) return { error: 'Enter your email address' };
    if (!value.includes('@')) return { error: 'Enter a valid email address' };
    return { identifier: value.toLowerCase() };
  }
  if (mode === 'phone') {
    if (!value) return { error: 'Enter your mobile number' };
    const e164 = value.includes('@') ? null : parseE164(value, countryCode);
    if (!e164) return { error: 'Enter a valid mobile number' };
    return { identifier: e164 };
  }
  if (!value) return { error: 'Enter your email or mobile number' };
  if (value.includes('@')) return { identifier: value.toLowerCase() };
  const e164 = parseE164(value, countryCode);
  if (!e164) return { error: 'Enter a valid email or mobile number' };
  return { identifier: e164 };
}

/**
 * Map a Firebase phone-auth error to a user-facing message. The
 * "already linked to a different account" case gets specific copy because it's a
 * common anti-duplicate signal support needs to recognise.
 */
export function phoneAuthErrorMessage(err) {
  if (err && err.code === 'auth/credential-already-in-use') {
    return 'This phone number is already linked to a different account.';
  }
  return (err && err.message) || 'Invalid code. Please try again.';
}
